import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PublicNavigation from '../../components/ui/PublicNavigation';
import { clientAuthService } from '../../services/clientAuthService';
import { supabase } from '../../lib/supabase';
import { ensureReferralAndWelcomePoints } from '../../services/referralService';

export default function ClientRegistration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const validateForm = () => {
    if (!formData?.name || !formData?.email || !formData?.phone || !formData?.password) {
      return 'Veuillez remplir tous les champs obligatoires';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      return 'Adresse email invalide';
    }

    // Phone validation
    const phoneRegex = /^(\+33|0)[1-9](\d{2}){4}$/;
    if (!phoneRegex?.test(formData?.phone?.replace(/\s/g, ''))) {
      return 'Numéro de téléphone invalide (format: +33612345678 ou 0612345678)';
    }

    // Password validation
    if (formData?.password?.length < 6) {
      return 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (formData?.password !== formData?.confirmPassword) {
      return 'Les mots de passe ne correspondent pas';
    }

    return null;
  };

  // Helper to read referral_code from cookie
  const getCookieReferralCode = () => {
    const match = document.cookie?.match(/(?:^|;\s*)referral_code=([^;]*)/);
    return match ? decodeURIComponent(match?.[1]) : null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const clientRecord = await clientAuthService?.signUp(
        formData?.name,
        formData?.email,
        formData?.phone,
        formData?.password
      );

      // Attach referral code if present in localStorage or cookie
      const storedRefCode = localStorage.getItem('adt_referral_code') || localStorage.getItem('referral_code') || getCookieReferralCode();
      if (storedRefCode && clientRecord?.id) {
        try {
          await supabase?.rpc('attach_referral_to_new_client', {
            p_new_client_id: clientRecord?.id,
            p_referral_code: storedRefCode
          });
          // Clear referral code after use
          localStorage.removeItem('adt_referral_code');
          localStorage.removeItem('referral_code');
          document.cookie = 'referral_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        } catch (refErr) {
          console.warn('[Registration] Could not attach referral:', refErr?.message);
        }
      }

      // Ensure referral code + welcome points are generated immediately
      if (clientRecord?.id) {
        try {
          await ensureReferralAndWelcomePoints(clientRecord?.id);
        } catch (ensureErr) {
          console.warn('[Registration] Could not ensure referral/welcome points:', ensureErr?.message);
        }
      }

      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Créer un compte - ADT Tracking</title>
        <meta name="description" content="Créez votre compte client ADT Tracking pour suivre vos envois" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />
        
        <div className="flex items-center justify-center px-4 py-16 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Créer mon compte
              </h1>
              <p className="text-gray-600">
                Rejoignez ADT Tracking et profitez de nos services
              </p>
            </div>

            {/* Registration Form Card */}
            <div className="bg-white rounded-lg shadow-md p-8">
              {success ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Compte créé avec succès !</h2>
                  <p className="text-gray-600 mb-6">
                    Votre compte a bien été créé. Vous pouvez maintenant vous connecter à votre espace client.
                  </p>
                  <Link
                    to="/client-login"
                    className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    Se connecter
                  </Link>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData?.name}
                    onChange={handleChange}
                    placeholder="Jean Dupont"
                    required
                    disabled={loading}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    required
                    disabled={loading}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Numéro de téléphone <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData?.phone}
                    onChange={handleChange}
                    placeholder="+33612345678"
                    required
                    disabled={loading}
                    className="w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Format: +33612345678 ou 0612345678
                  </p>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData?.password}
                      onChange={handleChange}
                      placeholder="Minimum 6 caractères"
                      required
                      disabled={loading}
                      className="w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirmez votre mot de passe"
                    required
                    disabled={loading}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Création du compte...
                    </span>
                  ) : (
                    'Créer mon compte'
                  )}
                </Button>

                <p className="text-xs text-gray-600 text-center">
                  En créant un compte, vous acceptez nos{' '}
                  <Link to="/shipping-information" className="text-green-600 hover:text-green-700 underline">
                    conditions d'utilisation
                  </Link>
                  {' '}et notre{' '}
                  <Link to="/shipping-information" className="text-green-600 hover:text-green-700 underline">
                    politique de confidentialité
                  </Link>
                </p>
              </form>
              )}
            </div>

            {/* Advantages */}
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-900 mb-3">Avantages du compte client</h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Suivez tous vos envois en un seul endroit</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Accumulez des points de fidélité</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Notifications automatiques par email</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Accès à vos documents et factures</span>
                </li>
              </ul>
            </div>

            {/* Footer Links */}
            <div className="mt-6 text-center space-y-2">
              <div className="text-sm text-gray-600">
                Vous avez déjà un compte ?{' '}
                <Link to="/login" className="text-green-600 hover:text-green-700 font-medium">
                  Se connecter
                </Link>
              </div>
              <div>
                <Link
                  to="/"
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}